import moment from 'moment';
import { dateTimeFormats } from '../config/constants';

export const getDateFromTimestamp = (timestamp) => new Date(timestamp * 1000);

export const getTimestampFromDate = (date) =>
  Math.round(new Date(date).getTime() / 1000);

// Unix seconds (UTC) for the start of the local day containing `date`.
// Used by the Inspect expression filters, which require date attributes as
// unix seconds. moment() resolves against the browser's timezone, so the
// returned instant is local midnight expressed in UTC seconds.
export const getStartOfDayTimestamp = (date) =>
  moment(date).startOf('day').unix();

// Unix seconds (UTC) for the end of the local day containing `date`
// (23:59:59 local). Pairs with getStartOfDayTimestamp to span a whole day.
export const getEndOfDayTimestamp = (date) =>
  moment(date).endOf('day').unix();

export const formatDate = (date, format = dateTimeFormats.DATE) =>
  moment(new Date(date)).format(format);

export const renderDateTime = (timestamp, format = dateTimeFormats.DATE_TIME) =>
  timestamp ? moment(new Date(timestamp * 1000)).format(format) : '';

// The IANA zone the browser is in. A scheduled recurrence rule is evaluated in
// an explicit zone; when a rule carries none yet (a new rule, or one migrated
// from the old interval-only fields) the browser's zone is the default.
export const getBrowserTimeZone = () => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone || 'UTC';
  } catch (e) {
    return 'UTC';
  }
};

// The two zoned helpers below share one Intl reader: a formatter for the zone
// (with or without the time fields) and a parser from formatToParts to plain
// numbers. Intl reports midnight as hour 24 under some ICU builds; normalise it.
const zonedFormatter = (timeZone, withTime) =>
  new Intl.DateTimeFormat(
    'en-US',
    Object.assign(
      { timeZone: timeZone, year: 'numeric', month: '2-digit', day: '2-digit' },
      withTime ? { hourCycle: 'h23', hour: '2-digit', minute: '2-digit', second: '2-digit' } : {},
    ),
  );

const zonedWallParts = (formatter, ms) => {
  const parts = formatter.formatToParts(new Date(ms));
  const map = {};
  for (let i = 0; i < parts.length; i++) {
    map[parts[i].type] = parts[i].value;
  }
  let hour = parseInt(map.hour, 10) || 0;
  if (hour === 24) hour = 0;
  return {
    year: parseInt(map.year, 10),
    month: parseInt(map.month, 10),
    day: parseInt(map.day, 10),
    hour: hour,
    minute: parseInt(map.minute, 10) || 0,
    second: parseInt(map.second, 10) || 0,
  };
};

// Unix seconds for a wall-clock instant (default noon) on `date`'s calendar
// day, resolved in an explicit IANA zone rather than the browser's own. A
// scheduled rule's clock is the rule's stored zone (see getBrowserTimeZone and
// the Time Zone control on the cadence editor), not wherever the browser
// happens to sit, so a date picked in the UI must be resolved against that
// zone to land on the calendar day the user actually meant. Computed via Intl
// (guess an instant, read back its wall time in the target zone, correct by
// the difference) rather than adding a timezone library -- modern
// Intl.DateTimeFormat already carries the full IANA database.
//
// Transition-aware: the zone's offset a day before and a day after the target
// are the two offsets that can apply to it, and each yields one candidate
// instant. A candidate whose wall time reads back as the target is valid; the
// earliest valid one wins (an ambiguous time in a fall-back overlap resolves to
// its first occurrence). If neither is valid the wall time falls in a
// spring-forward gap, and the pre-transition offset's candidate is used, which
// lands the gap's length later (02:30 in a 2am gap becomes 03:30). Both match
// the API engine's NodaTime AtLeniently. Sampling only the target's own offset
// is not enough: midnight Oct 4 2026 in Sydney, or midnight Apr 24 2026 in
// Cairo (whose DST starts AT midnight), resolve to 23:00 the day before.
export const getZonedTimestamp = (date, timeZone, hour = 12, minute = 0, second = 0) => {
  const d = new Date(date);
  const utcGuess = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), hour, minute, second);
  try {
    const formatter = zonedFormatter(timeZone, true);
    const wallAsIfUTC = (ms) => {
      const w = zonedWallParts(formatter, ms);
      return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
    };
    const offsetAt = (ms) => wallAsIfUTC(ms) - ms;
    const DAY = 24 * 60 * 60 * 1000;
    const offsetBefore = offsetAt(utcGuess - DAY);
    const offsetAfter = offsetAt(utcGuess + DAY);
    const candidates = [utcGuess - offsetBefore];
    if (offsetAfter !== offsetBefore) candidates.push(utcGuess - offsetAfter);
    const valid = candidates.filter((ms) => wallAsIfUTC(ms) === utcGuess);
    const resolved = valid.length ? Math.min.apply(null, valid) : utcGuess - offsetBefore;
    return Math.round(resolved / 1000);
  } catch (e) {
    return Math.round(utcGuess / 1000);
  }
};

// The calendar day `timestamp` (unix seconds) falls on in `timeZone`, as a Date
// at local midnight of that day -- the shape getZonedTimestamp takes back. Used
// to re-resolve an already-picked date when the rule it belongs to changes its
// zone or its coarseness, without moving the day the user actually chose.
export const getZonedCalendarDate = (timestamp, timeZone) => {
  const date = new Date(timestamp * 1000);
  try {
    const w = zonedWallParts(zonedFormatter(timeZone, false), date.getTime());
    return new Date(w.year, w.month - 1, w.day);
  } catch (e) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
};
